// src/pages/HelpDeskPage.js - Refactored with Common Components

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import styles from './HelpDesk.module.css';

// Common Components
import {
    Pagination,
    SearchBar,
    Toast,
    TableSkeleton,
    FormModal,
    ConfirmModal,
    EmptyState,
} from '../../components/common';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiHelpCircle,
    FiClock,
    FiUser,
    FiTag,
    FiCalendar,
    FiPlus,
    FiSend,
    FiFolder,
    FiFlag,
    FiTarget,
    FiZap,
    FiMoreHorizontal,
    FiChevronRight,
    FiSearch,
    FiMessageSquare,
    FiMail,
    FiCornerUpLeft,
    FiEye,
    FiClock as FiClockIcon,
    FiCheckCircle,
    FiCircle,
    FiRadio,
    FiCheckSquare,
    FiSend as FiSendIcon,
    FiMessageCircle,
    FiMinimize2,
    FiMaximize2,
} from 'react-icons/fi';
import { FaTicketAlt, FaRegClock, FaUserCircle } from 'react-icons/fa';

// ============================================
// CONSTANTS
// ============================================

const TICKETS_COLLECTION = 'help-desk-tickets';

const PRIORITY_OPTIONS = [
    { value: 'critico', label: 'Crítico', icon: '🔴' },
    { value: 'alto', label: 'Alta', icon: '🟠' },
    { value: 'medio', label: 'Média', icon: '🟡' },
    { value: 'baixo', label: 'Baixa', icon: '🟢' },
];

const IMPACT_OPTIONS = [
    { value: 'todos', label: 'Todos os usuários' },
    { value: 'departamento', label: 'Meu departamento' },
    { value: 'equipe', label: 'Minha equipe' },
    { value: 'individual', label: 'Apenas eu' },
];

const URGENCY_OPTIONS = [
    { value: 'imediato', label: 'Imediato' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'normal', label: 'Normal' },
    { value: 'baixa', label: 'Baixa' },
];

const CATEGORY_OPTIONS = [
    { value: 'acesso', label: 'Acesso' },
    { value: 'sistema', label: 'Sistema/Bug' },
    { value: 'duvida', label: 'Dúvida' },
    { value: 'melhoria', label: 'Melhoria' },
    { value: 'dados', label: 'Dados' },
    { value: 'integracao', label: 'Integração' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'rede', label: 'Rede' },
    { value: 'seguranca', label: 'Segurança' },
    { value: 'outro', label: 'Outro' },
];

const ITEMS_PER_PAGE = 5;

// ============================================
// HELPERS
// ============================================

const formatTimeSpent = (totalMinutes) => {
    if (!totalMinutes || totalMinutes < 0) return '0min';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
    }
    return `${minutes}min`;
};

const getTimeInStatus = (ticket) => {
    if (!ticket?.updatedAt?.toDate) return 'N/A';
    const diff = Date.now() - ticket.updatedAt.toDate().getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}min`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

// ============================================
// BADGE COMPONENT (kept as is - not in common)
// ============================================

const Badge = ({ text, type }) => {
    const getStatusClass = () => {
        if (type === 'status') {
            const classMap = {
                'Aberto': `${styles.statusBadge} ${styles.open}`,
                'Em Progresso': `${styles.statusBadge} ${styles.inProgress}`,
                'Fechado': `${styles.statusBadge} ${styles.closed}`,
                'Resolvido': `${styles.statusBadge} ${styles.resolved}`,
            };
            return classMap[text] || `${styles.statusBadge} ${styles.open}`;
        }
        if (type === 'priority') {
            const classMap = {
                'Crítico': `${styles.priorityBadge} ${styles.critical}`,
                'Alta': `${styles.priorityBadge} ${styles.high}`,
                'Média': `${styles.priorityBadge} ${styles.medium}`,
                'Baixa': `${styles.priorityBadge} ${styles.low}`,
            };
            return classMap[text] || `${styles.priorityBadge} ${styles.medium}`;
        }
        return '';
    };

    return <span className={getStatusClass()}>{text}</span>;
};

// ============================================
// CHAT PANEL COMPONENT (kept as is - complex)
// ============================================

const ChatPanel = ({
    ticket,
    onClose,
    onSendMessage,
    onCloseTicket,
    currentUser,
    loading,
    isMinimized,
    onToggleMinimize
}) => {
    const [message, setMessage] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const followUps = ticket?.followUps || [];

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [followUps]);

    useEffect(() => {
        if (!isMinimized) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isMinimized]);

    const handleSend = async () => {
        if (!message.trim() || loading) return;
        await onSendMessage(message.trim());
        setMessage('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const handleCloseTicket = async () => {
        if (!message.trim()) {
            alert('Adicione uma mensagem de resolução.');
            return;
        }
        if (window.confirm('Fechar este ticket?')) {
            setIsClosing(true);
            await onCloseTicket(message.trim());
            setIsClosing(false);
            onClose();
        }
    };

    if (!ticket) return null;

    return (
        <div className={`${styles.chatPanel} ${isMinimized ? styles.chatPanelMinimized : ''}`}>
            <div className={styles.chatHeader}>
                <div className={styles.chatHeaderInfo}>
                    <div className={styles.chatHeaderAvatar}>
                        <FiMessageCircle size={16} />
                    </div>
                    <div>
                        <div className={styles.chatHeaderTitle}>
                            #{ticket.id.substring(0, 6)}
                        </div>
                        <div className={styles.chatHeaderSubtitle}>
                            {ticket.userName}
                            <span className={styles.chatHeaderDot} />
                            <span className={ticket.status === 'Fechado' ? styles.statusClosed : styles.statusOpen}>
                                {ticket.status}
                            </span>
                        </div>
                    </div>
                </div>
                <div className={styles.chatHeaderActions}>
                    <button
                        onClick={onToggleMinimize}
                        className={styles.chatIconBtn}
                    >
                        {isMinimized ? <FiMaximize2 size={16} /> : <FiMinimize2 size={16} />}
                    </button>
                    <button
                        onClick={onClose}
                        className={styles.chatIconBtn}
                    >
                        <FiX size={18} />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <>
                    <div className={styles.chatMessages}>
                        {followUps.length === 0 ? (
                            <div className={styles.chatEmpty}>
                                <div className={styles.chatEmptyIcon}>💬</div>
                                <p>Nenhuma mensagem</p>
                            </div>
                        ) : (
                            followUps.map((msg, index) => {
                                const isFromUser = msg.isUserReply;
                                const isClosure = msg.type === 'closure';

                                return (
                                    <div
                                        key={index}
                                        className={`${styles.chatMessage} ${isFromUser ? styles.chatMessageUser : styles.chatMessageSupport
                                            } ${isClosure ? styles.chatMessageClosure : ''}`}
                                    >
                                        <div className={styles.chatMessageMeta}>
                                            <span className={styles.chatMessageName}>
                                                {isFromUser ? ticket.userName : 'Suporte'}
                                            </span>
                                            <span className={styles.chatMessageTime}>
                                                {msg.sentAt?.toDate?.()?.toLocaleTimeString('pt-BR', {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) || ''}
                                            </span>
                                        </div>
                                        <div className={styles.chatMessageText}>
                                            {msg.message}
                                        </div>
                                        {isClosure && (
                                            <div className={styles.chatMessageBadge}>
                                                <FiCheckCircle size={12} /> Resolvido
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {ticket.status !== 'Fechado' ? (
                        <div className={styles.chatInput}>
                            <div className={styles.chatInputRow}>
                                <textarea
                                    ref={inputRef}
                                    className={styles.chatInputField}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Digite sua mensagem..."
                                    rows="1"
                                    disabled={loading || isClosing}
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={loading || isClosing || !message.trim()}
                                    className={styles.chatSendBtn}
                                >
                                    {loading ? <span className={styles.buttonSpinner} /> : <FiSend size={18} />}
                                </button>
                            </div>
                            <div className={styles.chatInputFooter}>
                                <button
                                    onClick={handleCloseTicket}
                                    disabled={loading || isClosing || !message.trim()}
                                    className={styles.chatCloseBtn}
                                >
                                    <FiCheckCircle size={14} /> Fechar ticket
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.chatClosed}>
                            <FiCheckCircle size={16} />
                            <span>Ticket fechado</span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ============================================
// MAIN COMPONENT
// ============================================

const HelpDeskPage = () => {
    const { t } = useTranslation();
    const { logActivity } = useActivityLogger();

    // Form state
    const [assunto, setAssunto] = useState('');
    const [descricao, setDescricao] = useState('');
    const [categoria, setCategoria] = useState('');
    const [prioridade, setPrioridade] = useState('medio');
    const [impacto, setImpacto] = useState('individual');
    const [urgencia, setUrgencia] = useState('normal');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [expandedTicket, setExpandedTicket] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Chat state
    const [activeChat, setActiveChat] = useState(null);
    const [isChatMinimized, setIsChatMinimized] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [currentTicket, setCurrentTicket] = useState(null);
    const [editData, setEditData] = useState({});

    const { currentUser } = useAuth();
    const can = usePagePermissions('central_de_suporte');

    const {
        data: allTickets,
        loading: listLoading,
        error: listError,
        addDocument,
        deleteDocument,
        updateDocument
    } = useFirestore(TICKETS_COLLECTION, 'createdAt', 'desc');

    // ============================================
    // TOAST SYSTEM
    // ============================================

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
    }, []);

    // ============================================
    // USER TICKETS
    // ============================================

    const userTickets = useMemo(() => {
        if (!currentUser?.email || !allTickets) return [];
        return allTickets.filter(ticket => ticket.userEmail === currentUser.email);
    }, [allTickets, currentUser]);

    // ============================================
    // PAGINATION
    // ============================================

    const paginatedTickets = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return userTickets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [userTickets, currentPage]);

    const totalPages = Math.ceil(userTickets.length / ITEMS_PER_PAGE);

    // Reset page when data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [userTickets.length]);

    // ============================================
    // STATS
    // ============================================

    const stats = useMemo(() => {
        if (!userTickets.length) return { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 };

        return {
            total: userTickets.length,
            open: userTickets.filter(t => t.status === 'Aberto').length,
            inProgress: userTickets.filter(t => t.status === 'Em Progresso').length,
            resolved: userTickets.filter(t => t.status === 'Resolvido' || t.status === 'Fechado').length,
            closed: userTickets.filter(t => t.status === 'Fechado').length,
        };
    }, [userTickets]);

    // ============================================
    // PRIORITY CALCULATOR
    // ============================================

    const calculatePriorityLevel = useCallback((prioridade, urgencia, impacto) => {
        const priorityScore = { critico: 4, alto: 3, medio: 2, baixo: 1 };
        const urgencyScore = { imediato: 4, urgente: 3, normal: 2, baixa: 1 };
        const impactScore = { todos: 4, departamento: 3, equipe: 2, individual: 1 };

        const total = (priorityScore[prioridade] || 0) +
            (urgencyScore[urgencia] || 0) +
            (impactScore[impacto] || 0);

        if (total >= 10) return 'Crítico';
        if (total >= 8) return 'Alta';
        if (total >= 6) return 'Média';
        return 'Baixa';
    }, []);

    // ============================================
    // CREATE TICKET
    // ============================================

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!can.create) {
            showToast(t('pages.helpdesk.noPermissionCreate'), 'error');
            return;
        }

        setLoading(true);
        setError('');

        if (!assunto || !descricao) {
            setError(t('pages.helpdesk.form.subjectRequired'));
            setLoading(false);
            return;
        }

        const priorityLevel = calculatePriorityLevel(prioridade, urgencia, impacto);

        const newTicketData = {
            assunto: assunto.trim(),
            descricao: descricao.trim(),
            categoria,
            prioridade: priorityLevel,
            prioridadeRaw: prioridade,
            urgencia,
            impacto,
            status: 'Aberto',
            userEmail: currentUser.email,
            userName: currentUser.nome || currentUser.email?.split('@')[0] || 'Usuário ERP',
            userRole: currentUser.role || 'Básico',
            assignedToEmail: null,
            assignedToName: null,
            timeSpentMinutes: 0,
            followUps: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            history: [
                {
                    action: 'created',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Ticket criado'
                }
            ]
        };

        try {
            const result = await addDocument(newTicketData);
            await logActivity(
                'ticket_created',
                `help-desk-ticket/${result.id}`,
                `Ticket "${assunto.trim()}" criado (Categoria: ${categoria || 'N/A'}, Prioridade: ${priorityLevel})`
            );

            showToast('Ticket criado com sucesso! ✨', 'success');
            setAssunto('');
            setDescricao('');
            setCategoria('');
            setPrioridade('medio');
            setImpacto('individual');
            setUrgencia('normal');
        } catch (err) {
            console.error("Erro ao enviar ticket:", err);
            setError(t('pages.helpdesk.createError'));
            showToast(t('pages.helpdesk.createError'), 'error');
        } finally {
            setLoading(false);
        }
    }, [assunto, descricao, categoria, prioridade, urgencia, impacto, currentUser,
        addDocument, showToast, can.create, calculatePriorityLevel, t]);

    // ============================================
    // CHAT HANDLERS
    // ============================================

    const handleOpenChat = useCallback((ticket) => {
        if (ticket.status === 'Fechado') {
            showToast('Este ticket já foi fechado.', 'warning');
            return;
        }
        setActiveChat(ticket);
        setIsChatMinimized(false);
    }, [showToast]);

    const handleCloseChat = useCallback(() => {
        setActiveChat(null);
        setIsChatMinimized(false);
    }, []);

    const handleToggleMinimize = useCallback(() => {
        setIsChatMinimized(prev => !prev);
    }, []);

    const handleSendChatMessage = useCallback(async (message) => {
        if (!activeChat) return;

        setLoading(true);

        const followUpEntry = {
            message: message,
            sentBy: currentUser.nome || currentUser.email,
            sentByEmail: currentUser.email,
            sentAt: new Date(),
            type: 'user_reply',
            isUserReply: true,
        };

        const updateData = {
            updatedAt: new Date(),
            updatedBy: currentUser.email,
            followUps: [...(activeChat.followUps || []), followUpEntry],
            history: [
                ...(activeChat.history || []),
                {
                    action: 'user_replied',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Usuário respondeu ao suporte'
                }
            ],
            ...(activeChat.status === 'Fechado' ? { status: 'Em Progresso' } : {}),
        };

        try {
            const updatedTicket = await updateDocument(activeChat.id, updateData);
            setActiveChat(prev => ({
                ...prev,
                ...updateData,
                followUps: [...(prev.followUps || []), followUpEntry]
            }));
            showToast('Mensagem enviada! 📨', 'success');
        } catch (err) {
            console.error("Erro ao enviar mensagem:", err);
            showToast('Erro ao enviar mensagem', 'error');
        } finally {
            setLoading(false);
        }
    }, [activeChat, currentUser, updateDocument, showToast]);

    const handleCloseTicketFromChat = useCallback(async (message) => {
        if (!activeChat) return;

        setLoading(true);

        const followUpEntry = {
            message: message,
            sentBy: currentUser.nome || currentUser.email,
            sentByEmail: currentUser.email,
            sentAt: new Date(),
            type: 'closure',
            isUserReply: true,
        };

        const updateData = {
            status: 'Fechado',
            resolution: message,
            updatedAt: new Date(),
            updatedBy: currentUser.email,
            followUps: [...(activeChat.followUps || []), followUpEntry],
            history: [
                ...(activeChat.history || []),
                {
                    action: 'closed_by_user',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Ticket fechado pelo usuário'
                }
            ]
        };

        try {
            await updateDocument(activeChat.id, updateData);

            await logActivity(
                'ticket_closed',
                `help-desk-ticket/${activeChat.id}`,
                `Ticket "${activeChat.assunto}" fechado pelo usuário`
            );

            showToast('Ticket fechado com sucesso! ✅', 'success');
            handleCloseChat();
        } catch (err) {
            console.error("Erro ao fechar ticket:", err);
            showToast('Erro ao fechar ticket', 'error');
        } finally {
            setLoading(false);
        }
    }, [activeChat, currentUser, updateDocument, showToast, handleCloseChat]);

    // ============================================
    // DELETE HANDLER
    // ============================================

    const handleDelete = useCallback(async (ticket) => {
        if (!can.delete) {
            showToast(t('pages.helpdesk.noPermissionDelete'), 'error');
            return;
        }

        if (ticket.assignedToName) {
            showToast(t('pages.helpdesk.cannotDeleteAssigned'), 'warning');
            return;
        }

        setLoading(true);
        try {
            await deleteDocument(ticket.id);

             await logActivity(
            'ticket_deleted',
            `help-desk-ticket/${ticket.id}`,
            `Ticket "${ticket.assunto}" excluído (ID: ${ticket.id.substring(0, 8)})`
        );

            showToast('Ticket excluído com sucesso 🗑️', 'success');
        } catch (err) {
            console.error("Erro ao excluir ticket:", err);
            showToast(t('pages.helpdesk.deleteError'), 'error');
        } finally {
            setLoading(false);
        }
    }, [deleteDocument, showToast, can.delete, t]);

    // ============================================
    // EDIT HANDLERS
    // ============================================

    const handleEdit = useCallback((ticket) => {
        if (!can.edit) {
            showToast(t('pages.helpdesk.noPermissionEdit'), 'error');
            return;
        }

        setCurrentTicket(ticket);
        setEditData({
            assunto: ticket.assunto,
            descricao: ticket.descricao,
            categoria: ticket.categoria || '',
            prioridade: ticket.prioridadeRaw || 'medio',
            urgencia: ticket.urgencia || 'normal',
            impacto: ticket.impacto || 'individual',
        });
        setIsEditing(true);
        setError('');
    }, [can.edit, showToast, t]);

    const handleCloseEdit = useCallback(() => {
        setIsEditing(false);
        setCurrentTicket(null);
        setEditData({});
    }, []);

    const handleSaveEdit = useCallback(async (e) => {
        e.preventDefault();

        if (!can.edit) {
            showToast(t('pages.helpdesk.noPermissionEdit'), 'error');
            return;
        }

        setLoading(true);
        setError('');

        if (!editData.assunto?.trim() || !editData.descricao?.trim()) {
            setError(t('pages.helpdesk.form.subjectRequired'));
            setLoading(false);
            return;
        }

        const priorityLevel = calculatePriorityLevel(
            editData.prioridade,
            editData.urgencia,
            editData.impacto
        );

        const updatedData = {
            assunto: editData.assunto.trim(),
            descricao: editData.descricao.trim(),
            categoria: editData.categoria,
            prioridade: priorityLevel,
            prioridadeRaw: editData.prioridade,
            urgencia: editData.urgencia,
            impacto: editData.impacto,
            updatedAt: new Date(),
            history: [
                ...(currentTicket.history || []),
                {
                    action: 'edited',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Ticket editado'
                }
            ]
        };

        try {
            await updateDocument(currentTicket.id, updatedData);

            await logActivity(
                'ticket_updated',
                `help-desk-ticket/${currentTicket.id}`,
                `Ticket "${editData.assunto.trim()}" atualizado (Prioridade: ${priorityLevel}, Categoria: ${editData.categoria || 'N/A'})`
            );

            showToast('Ticket atualizado com sucesso! ✨', 'success');
            handleCloseEdit();
        } catch (err) {
            console.error("Erro ao atualizar ticket:", err);
            setError(t('pages.helpdesk.updateError'));
            showToast(t('pages.helpdesk.updateError'), 'error');
        } finally {
            setLoading(false);
        }
    }, [editData, currentTicket, currentUser, updateDocument, showToast, handleCloseEdit, can.edit, calculatePriorityLevel, t]);

    // ============================================
    // TOGGLE EXPAND
    // ============================================

    const toggleExpand = useCallback((ticketId) => {
        setExpandedTicket(prev => prev === ticketId ? null : ticketId);
    }, []);

    // ============================================
    // TICKET CARD COMPONENT
    // ============================================

    const TicketCard = useCallback(({ ticket }) => {
        const isExpanded = expandedTicket === ticket.id;
        const followUps = ticket.followUps || [];
        const hasFollowUps = followUps.length > 0;
        const supportFollowUps = followUps.filter(f => !f.isUserReply);
        const hasSupportFollowUps = supportFollowUps.length > 0;
        const timeInStatus = getTimeInStatus(ticket);
        const isChatOpen = activeChat?.id === ticket.id;

        const handleKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleExpand(ticket.id);
            }
        };

        return (
            <div className={styles.ticketCard}>
                <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-controls={`ticket-content-${ticket.id}`}
                    aria-label={`Ticket: ${ticket.assunto}, status: ${ticket.status}`}
                    onClick={() => toggleExpand(ticket.id)}
                    onKeyDown={handleKeyDown}
                    className={styles.ticketCardHeader}
                >
                    <div className={styles.ticketCardContent}>
                        <div className={styles.ticketCardTitle}>
                            <span className={styles.ticketTitle}>{ticket.assunto}</span>
                            <Badge text={ticket.status} type="status" />
                            <Badge text={ticket.prioridade} type="priority" />
                            {hasFollowUps && (
                                <span className={styles.followUpBadge} title={`${followUps.length} mensagem(ns)`}>
                                    <FiMessageSquare size={12} aria-hidden="true" /> {followUps.length}
                                </span>
                            )}
                            {hasSupportFollowUps && ticket.status !== 'Fechado' && (
                                <span className={styles.newReplyBadge} title="Há mensagens do suporte aguardando resposta">
                                    <FiMail size={12} aria-hidden="true" /> Nova
                                </span>
                            )}
                        </div>
                        <div className={styles.ticketCardMeta}>
                            <span>#{ticket.id.substring(0, 8)}</span>
                            <span>•</span>
                            <span>{ticket.createdAt?.toDate().toLocaleDateString('pt-BR')}</span>
                            {ticket.categoria && (
                                <>
                                    <span>•</span>
                                    <span>{CATEGORY_OPTIONS.find(c => c.value === ticket.categoria)?.label || ticket.categoria}</span>
                                </>
                            )}
                            <span>•</span>
                            <span className={styles.ticketCardTime}>
                                <FiClock size={12} aria-hidden="true" /> {formatTimeSpent(ticket.timeSpentMinutes)}
                            </span>
                            {ticket.status !== 'Fechado' && (
                                <>
                                    <span>•</span>
                                    <span className={styles.ticketCardTime}>
                                        <FiClockIcon size={12} aria-hidden="true" />
                                        <strong>Espera:</strong> {timeInStatus}
                                    </span>
                                </>
                            )}
                            {ticket.assignedToName && (
                                <>
                                    <span>•</span>
                                    <span className={styles.ticketCardUser}>
                                        <FiUser size={12} aria-hidden="true" /> {ticket.assignedToName}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={styles.ticketCardActions}>
                        <div className={styles.actionGroup}>
                            {ticket.status !== 'Fechado' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenChat(ticket);
                                    }}
                                    disabled={loading}
                                    className={`${styles.actionBtn} ${isChatOpen ? styles.chatActive : styles.chat}`}
                                    title="Abrir chat"
                                    aria-label="Abrir chat do ticket"
                                >
                                    <FiMessageCircle aria-hidden="true" />
                                    {isChatOpen && <span className={styles.chatIndicator} />}
                                </button>
                            )}
                            {can.edit && ticket.status !== 'Fechado' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleEdit(ticket); }}
                                    disabled={loading}
                                    className={`${styles.actionBtn} ${styles.edit}`}
                                    title="Editar"
                                    aria-label="Editar ticket"
                                >
                                    <FiEdit aria-hidden="true" />
                                </button>
                            )}
                            {can.delete && !ticket.assignedToName && ticket.status !== 'Fechado' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(ticket); }}
                                    disabled={loading}
                                    className={`${styles.actionBtn} ${styles.delete}`}
                                    title="Excluir"
                                    aria-label="Excluir ticket"
                                >
                                    <FiTrash2 aria-hidden="true" />
                                </button>
                            )}
                        </div>
                        <FiChevronRight
                            className={isExpanded ? styles.chevronExpanded : styles.chevron}
                            aria-hidden="true"
                        />
                    </div>
                </div>
                {isExpanded && (
                    <div
                        id={`ticket-content-${ticket.id}`}
                        className={styles.ticketCardExpanded}
                        role="region"
                        aria-labelledby={`ticket-title-${ticket.id}`}
                    >
                        <div className={styles.ticketCardDescription}>
                            {ticket.descricao}
                        </div>

                        {hasFollowUps && (
                            <div className={styles.userFollowUpSection}>
                                <div className={styles.followUpSectionHeader}>
                                    <FiMessageSquare size={16} aria-hidden="true" />
                                    <span>Histórico de mensagens ({followUps.length})</span>
                                </div>
                                {followUps.map((followUp, index) => {
                                    const isFromSupport = !followUp.isUserReply;
                                    const isClosure = followUp.type === 'closure';

                                    return (
                                        <div
                                            key={index}
                                            className={`${styles.userFollowUpItem} ${isClosure ? styles.closureItem : ''} ${!isFromSupport ? styles.userReplyItem : ''}`}
                                        >
                                            <div className={styles.followUpHeader}>
                                                <span className={styles.followUpSender}>
                                                    <FiUser size={12} aria-hidden="true" /> {followUp.sentBy}
                                                    {!isFromSupport && (
                                                        <span className={styles.youBadge}>(Você)</span>
                                                    )}
                                                </span>
                                                <span className={styles.followUpDate}>
                                                    <FiClock size={12} aria-hidden="true" />
                                                    {followUp.sentAt?.toDate?.()?.toLocaleString('pt-BR') || 'N/A'}
                                                </span>
                                                {isClosure && (
                                                    <span className={styles.closureBadge}>🔒 Fechamento</span>
                                                )}
                                                {isFromSupport && !isClosure && (
                                                    <span className={styles.supportBadge}>Suporte</span>
                                                )}
                                            </div>
                                            <div className={styles.userFollowUpMessage}>
                                                {followUp.message}
                                            </div>
                                        </div>
                                    );
                                })}

                                {ticket.status !== 'Fechado' && (
                                    <button
                                        onClick={() => handleOpenChat(ticket)}
                                        className={styles.chatButton}
                                    >
                                        <FiMessageCircle size={16} aria-hidden="true" /> Abrir Chat
                                    </button>
                                )}
                            </div>
                        )}

                        <div className={styles.ticketCardExtra}>
                            {ticket.urgencia && (
                                <span>Urgência: {URGENCY_OPTIONS.find(u => u.value === ticket.urgencia)?.label || ticket.urgencia}</span>
                            )}
                            {ticket.impacto && (
                                <span>Impacto: {IMPACT_OPTIONS.find(i => i.value === ticket.impacto)?.label || ticket.impacto}</span>
                            )}
                            {ticket.assignedToName && (
                                <span>Atribuído a: {ticket.assignedToName}</span>
                            )}
                            {ticket.status === 'Fechado' && (
                                <span className={styles.resolvedBadge}>✅ Resolvido</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [expandedTicket, toggleExpand, loading, can.edit, can.delete, activeChat, handleOpenChat, handleEdit, handleDelete]);

    // ============================================
    // EDIT MODAL
    // ============================================

    const EditModal = () => {
        if (!isEditing || !currentTicket) return null;

        return (
            <FormModal
                isOpen={isEditing}
                onClose={handleCloseEdit}
                onSubmit={handleSaveEdit}
                title="Editar Ticket"
                loading={loading}
                submitText="Salvar"
                cancelText="Cancelar"
                icon={<FiEdit size={24} />}
            >
                <div className={styles.formGroup}>
                    <label htmlFor="edit-assunto">
                        Assunto <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="edit-assunto"
                        type="text"
                        className={styles.formInput}
                        value={editData.assunto || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, assunto: e.target.value }))}
                        disabled={loading}
                        required
                        placeholder="Digite o assunto do ticket"
                    />
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="edit-descricao">
                        Descrição <span className={styles.required}>*</span>
                    </label>
                    <textarea
                        id="edit-descricao"
                        className={styles.formTextarea}
                        value={editData.descricao || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, descricao: e.target.value }))}
                        disabled={loading}
                        rows="4"
                        required
                        placeholder="Descreva detalhadamente o problema..."
                    />
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label htmlFor="edit-categoria">Categoria</label>
                        <select
                            id="edit-categoria"
                            className={styles.formSelect}
                            value={editData.categoria || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, categoria: e.target.value }))}
                            disabled={loading}
                        >
                            <option value="">Selecione uma categoria</option>
                            {CATEGORY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="edit-prioridade">Prioridade</label>
                        <select
                            id="edit-prioridade"
                            className={styles.formSelect}
                            value={editData.prioridade || 'medio'}
                            onChange={(e) => setEditData(prev => ({ ...prev, prioridade: e.target.value }))}
                            disabled={loading}
                        >
                            {PRIORITY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label htmlFor="edit-urgencia">Urgência</label>
                        <select
                            id="edit-urgencia"
                            className={styles.formSelect}
                            value={editData.urgencia || 'normal'}
                            onChange={(e) => setEditData(prev => ({ ...prev, urgencia: e.target.value }))}
                            disabled={loading}
                        >
                            {URGENCY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="edit-impacto">Impacto</label>
                        <select
                            id="edit-impacto"
                            className={styles.formSelect}
                            value={editData.impacto || 'individual'}
                            onChange={(e) => setEditData(prev => ({ ...prev, impacto: e.target.value }))}
                            disabled={loading}
                        >
                            {IMPACT_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {error && <div className={styles.errorHint} role="alert"><FiAlertCircle size={16} aria-hidden="true" /> {error}</div>}
            </FormModal>
        );
    };

    // ============================================
    // MAIN RENDER
    // ============================================

    return (
        <div className={styles.pageContainer}>
            {/* Toast using common component */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                    duration={5000}
                />
            )}

            {/* Chat Panel */}
            {activeChat && (
                <ChatPanel
                    ticket={activeChat}
                    onClose={handleCloseChat}
                    onSendMessage={handleSendChatMessage}
                    onCloseTicket={handleCloseTicketFromChat}
                    currentUser={currentUser}
                    loading={loading}
                    isMinimized={isChatMinimized}
                    onToggleMinimize={handleToggleMinimize}
                />
            )}

            {/* Form Section */}
            <div id="form-section" className={styles.formSection}>
                <div className={styles.formSectionHeader}>
                    <h3>{t('pages.helpdesk.newTicket')}</h3>
                </div>

                {!can.create ? (
                    <div className={styles.errorContainer} role="alert">
                        <p className={styles.errorText}><FiAlertCircle size={20} aria-hidden="true" /> {t('pages.helpdesk.noPermissionCreate')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className={styles.formGroup}>
                            <label htmlFor="assunto">
                                Assunto <span className={styles.required}>*</span>
                            </label>
                            <input
                                id="assunto"
                                type="text"
                                className={styles.formInput}
                                value={assunto}
                                onChange={(e) => setAssunto(e.target.value)}
                                disabled={loading}
                                placeholder="Ex: Problema ao acessar o sistema"
                                required
                                aria-required="true"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="descricao">
                                Descrição <span className={styles.required}>*</span>
                            </label>
                            <textarea
                                id="descricao"
                                className={styles.formTextarea}
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                disabled={loading}
                                placeholder="Descreva detalhadamente o problema..."
                                rows="4"
                                required
                                aria-required="true"
                            />
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label htmlFor="categoria">Categoria</label>
                                <select
                                    id="categoria"
                                    className={styles.formSelect}
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Selecione uma categoria</option>
                                    {CATEGORY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="prioridade">
                                    Prioridade <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="prioridade"
                                    className={styles.formSelect}
                                    value={prioridade}
                                    onChange={(e) => setPrioridade(e.target.value)}
                                    disabled={loading}
                                >
                                    {PRIORITY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label htmlFor="urgencia">
                                    Urgência <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="urgencia"
                                    className={styles.formSelect}
                                    value={urgencia}
                                    onChange={(e) => setUrgencia(e.target.value)}
                                    disabled={loading}
                                >
                                    {URGENCY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="impacto">
                                    Impacto <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="impacto"
                                    className={styles.formSelect}
                                    value={impacto}
                                    onChange={(e) => setImpacto(e.target.value)}
                                    disabled={loading}
                                >
                                    {IMPACT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {error && <div className={styles.errorHint} role="alert"><FiAlertCircle size={16} aria-hidden="true" /> {error}</div>}

                        <button
                            type="submit"
                            disabled={loading}
                            className={styles.submitBtn}
                            style={{ marginTop: '8px' }}
                        >
                            {loading ? <span className={styles.buttonSpinner} /> : <><FiSendIcon size={18} aria-hidden="true" /> Enviar Ticket</>}
                        </button>
                    </form>
                )}
            </div>

            {/* Ticket List */}
            <div className={styles.ticketListHeader}>
                <div className={styles.ticketListTitle}>
                    <FaTicketAlt size={20} aria-hidden="true" />
                    <h3>{t('pages.helpdesk.yourTickets')}</h3>
                    <span className={styles.ticketCount}>{userTickets.length}</span>
                </div>
            </div>

            {listLoading ? (
                <TableSkeleton columns={4} rows={5} />
            ) : listError ? (
                <div className={styles.errorContainer} role="alert">
                    <p className={styles.errorText}><FiAlertCircle size={20} aria-hidden="true" /> {t('pages.helpdesk.error', { error: listError.message })}</p>
                </div>
            ) : userTickets.length === 0 ? (
                <EmptyState
                    icon="🎫"
                    title="Nenhum ticket encontrado"
                    description="Os tickets aparecerão aqui quando forem criados pelos usuários."
                />
            ) : (
                <>
                    <div className={styles.ticketList}>
                        {paginatedTickets.map(ticket => (
                            <TicketCard key={ticket.id} ticket={ticket} />
                        ))}
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={userTickets.length}
                        onPageChange={setCurrentPage}
                        itemsLabel="tickets"
                    />
                </>
            )}

            <EditModal />
        </div>
    );
};

export default HelpDeskPage;